/**
 * Auth redirects for Supabase (OAuth, email confirm, password reset).
 *
 * Use **`/auth/callback?next=/dashboard`** so Supabase returns `?code=` and `app/auth/callback/route.ts`
 * exchanges it for a session (PKCE). Add your exact callback URL to Supabase → Authentication → URL Configuration
 * → Redirect URLs, e.g. `https://yourdomain.com/auth/callback` and `http://localhost:3000/auth/callback`.
 *
 * Google / Apple sign-in does not use a separate email confirmation step — the provider verifies identity.
 *
 * Set `NEXT_PUBLIC_APP_URL` in production so server-rendered links match your live host.
 *
 * Full dashboard checklist (providers, redirect URLs, Apple Developer): **LAUNCH_HARDENING.md** → section **9**.
 */

function getSiteOrigin(): string {
  const configured = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_APP_URL?.trim() : "";
  if (configured) {
    try {
      return new URL(configured).origin;
    } catch {
      const o = configured.replace(/\/+$/, "");
      if (o.startsWith("http://") || o.startsWith("https://")) {
        try {
          return new URL(o).origin;
        } catch {
          return o;
        }
      }
      return `https://${o}`;
    }
  }
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

/**
 * Full URL to the auth callback handler. `next` is where the user lands after a successful exchange (default dashboard).
 */
export function getAuthCallbackUrl(nextPath: string = "/dashboard"): string {
  const next = nextPath.startsWith("/") ? nextPath : `/${nextPath}`;
  const qs = new URLSearchParams({ next });
  const origin = getSiteOrigin();
  if (!origin) {
    return `/auth/callback?${qs.toString()}`;
  }
  return `${origin}/auth/callback?${qs.toString()}`;
}

/**
 * Redirect target for email confirmation and OAuth — callback exchanges `code`, then sends user to dashboard.
 */
export function getAuthRedirectTo(): string {
  return getAuthCallbackUrl("/dashboard");
}

/**
 * Password reset emails — after exchange, user lands on `/reset-password` to choose a new password.
 */
export function getPasswordResetRedirectTo(): string {
  return getAuthCallbackUrl("/reset-password");
}
